import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { getStaffQuarterInventorySummary } from "../../../../core/data/redux/staffQuarterSlice";
import { Card, Row, Col, Statistic, Tag, Breadcrumb, Skeleton, Progress } from "antd";

const InventoryDashboard = () => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();

    const [locations, setLocations] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        dispatch(getStaffQuarterInventorySummary({})).then((res: any) => {
            if (res.payload?.success && Array.isArray(res.payload.data)) {
                setLocations(res.payload.data);
            }
            setIsLoading(false);
        });
    }, [dispatch]);

    const filteredLocations = locations
        .filter(loc => 
            (loc.locationName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (loc.locationCode || "").toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => (a.locationName || "").localeCompare(b.locationName || ""));

    const totalVacant = locations.reduce((sum, loc) => sum + (loc.vacant || 0), 0);
    const totalVacantInitiated = locations.reduce((sum, loc) => sum + (loc.vacationInitiated || 0), 0);
    const totalOccupied = locations.reduce((sum, loc) => sum + (loc.occupied || 0), 0);
    const totalFlats = locations.reduce((sum, loc) => sum + (loc.total || 0), 0);

    const stats = [
        { 
            title: "Vacant Units", 
            count: totalVacant, 
            color: "#E30613", 
            icon: "ti-home-off",
            desc: "Ready for allotment"
        },
        {
            title: "Vacant Initiated",
            count: totalVacantInitiated,
            color: "#007bff",
            icon: "ti-history",
            desc: "In-process vacation"
        },
        {
            title: "Occupied Units",
            count: totalOccupied,
            color: "#28a745",
            icon: "ti-home-check",
            desc: "Currently assigned"
        },
        {
            title: "Total Capacity",
            count: totalFlats,
            color: "#64748b",
            icon: "ti-building-community",
            desc: "Across all locations",
            link: routes.staffQuarter.inventory.locations,
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Modern Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div>
                        <h3 className="page-title mb-1" style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a' }}>Inventory Overview</h3>
                        <Breadcrumb items={[
                            { title: <Link to={routes.staffQuarter.inventory.dashboard}>Staff Quarter</Link> },
                            { title: 'Inventory Dashboard' }
                        ]} />
                    </div>
                    <div className="d-flex gap-2 mt-md-0 mt-3">
                        <Tag color="red" style={{ padding: '4px 12px', borderRadius: '20px', fontWeight: 600 }}>{locations.length} Locations Tracking</Tag>
                    </div>
                </div>

                {/* Standardized Premium Stats */}
                <div className="row mb-4 g-3">
                    {stats.map((stat, index) => (
                        <div className="col-xl-3 col-sm-6 col-12" key={index}>
                            <div className="card border-0 shadow-soft h-100 premium-stat-card" style={{ borderRadius: '12px' }}>
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="icon-wrapper d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', background: `${stat.color}10`, borderRadius: '10px' }}>
                                            <i className={`ti ${stat.icon}`} style={{ fontSize: '22px', color: stat.color }}></i>
                                        </div>
                                        <div className="text-end">
                                            <div className="text-muted small fw-medium text-uppercase mb-1" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>{stat.title}</div>
                                            <h2 className="mb-0 fw-bold" style={{ fontSize: '28px', color: '#0f172a' }}>{stat.count}</h2>
                                        </div>
                                    </div>
                                    <Progress 
                                        percent={Math.round((stat.count / (totalFlats || 1)) * 100)} 
                                        size="small" 
                                        strokeColor={stat.color} 
                                        showInfo={false} 
                                        trailColor="#f1f5f9"
                                    />
                                    <div className="mt-2 d-flex justify-content-between align-items-center">
                                        <span className="text-muted small" style={{ fontSize: '11px' }}>{stat.desc}</span>
                                        <span className="fw-bold text-dark small" style={{ fontSize: '11px' }}>{Math.round((stat.count / (totalFlats || 1)) * 100)}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-2 mb-md-0 mb-3">
                        <div style={{ width: '3px', height: '20px', background: '#E30613', borderRadius: '2px' }}></div>
                        <h4 className="mb-0 fw-bold" style={{ fontSize: '18px', color: '#0f172a' }}>Geographic Breakdown</h4>
                    </div>
                    <div style={{ width: '300px' }}>
                        <div className="input-group search-group">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="ti ti-search text-muted"></i>
                            </span>
                            <input 
                                type="text" 
                                className="form-control border-start-0 ps-0" 
                                placeholder="Search location or code..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ boxShadow: 'none' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Location Cards V3 */}
                {isLoading ? (
                    <div className="row g-4">
                        {[1, 2, 3].map(i => (
                            <div className="col-xl-4 col-md-6 col-12" key={i}>
                                <Skeleton active avatar paragraph={{ rows: 3 }} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="row g-4">
                        {filteredLocations.map((loc, index) => (
                            <div className="col-xl-4 col-md-6 col-12" key={index}>
                                <Link to={`${routes.staffQuarter.inventory.locationView}?location=${loc.locationName}&code=${loc.locationCode}`}>
                                    <div className="card h-100 border-0 shadow-soft location-premium-card" style={{ borderRadius: '12px', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                                        <div className="card-body p-0">
                                            <div className="p-4 border-bottom d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-light p-2 rounded-3">
                                                        <i className="ti ti-map-pin-filled text-danger" style={{ fontSize: '20px' }}></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-0 fw-bold" style={{ fontSize: '16px', color: '#0f172a' }}>{loc.locationName}</h5>
                                                        <span className="text-muted small">Code: {loc.locationCode}</span>
                                                    </div>
                                                </div>
                                                <div className="go-btn rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', background: '#f8fafc' }}>
                                                    <i className="ti ti-chevron-right text-muted" style={{ fontSize: '14px' }}></i>
                                                </div>
                                            </div>
                                            <div className="p-4 bg-white">
                                                <div className="row text-center g-0">
                                                    <div className="col-4 border-end">
                                                        <div className="small text-muted mb-1" style={{ fontSize: '10px' }}>VACANT</div>
                                                        <div className="fw-bold text-danger h5 mb-0">{loc.vacant || 0}</div>
                                                    </div>
                                                    <div className="col-4 border-end">
                                                        <div className="small text-muted mb-1" style={{ fontSize: '10px' }}>INITIATED</div>
                                                        <div className="fw-bold text-primary h5 mb-0">{loc.vacationInitiated || 0}</div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="small text-muted mb-1" style={{ fontSize: '10px' }}>OCCUPIED</div>
                                                        <div className="fw-bold text-success h5 mb-0">{loc.occupied || 0}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="px-4 pb-4">
                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                    <span className="text-muted small fw-medium">Occupancy Load</span>
                                                    <span className="fw-bold text-dark small">{Math.round((loc.occupied / (loc.total || 1)) * 100)}%</span>
                                                </div>
                                                <Progress 
                                                    percent={Math.round((loc.occupied / (loc.total || 1)) * 100)} 
                                                    size="small" 
                                                    strokeColor="#28a745" 
                                                    showInfo={false} 
                                                    trailColor="#f1f5f9"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .shadow-soft {
                    box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.05), 0 2px 10px -3px rgba(0, 0, 0, 0.02) !important;
                    border: 1px solid #f1f5f9 !important;
                }
                .premium-stat-card {
                    background: linear-gradient(to bottom right, #ffffff, #fcfcfc);
                }
                .location-premium-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02) !important;
                    border-color: #E3061330 !important;
                }
                .location-premium-card:hover .go-btn {
                    background: #E30613 !important;
                }
                .location-premium-card:hover .go-btn i {
                    color: #fff !important;
                }
                .icon-wrapper {
                    transition: all 0.3s ease;
                }
                .premium-stat-card:hover .icon-wrapper {
                    transform: scale(1.1);
                }
            `}</style>
        </div>
    );
};

export default InventoryDashboard;
